/**
 * E2EE Vault crypto primitives (Task #84).
 *
 * Everything in this module runs in the browser. The server only ever sees
 * opaque base64 ciphertext + KDF parameters; it cannot derive plaintext.
 *
 * - KDF:          Argon2id via hash-wasm (WASM, ~64 KiB).
 * - Wrap/Unwrap:  AES-256-GCM via Web Crypto SubtleCrypto.
 * - Identity:     X25519 (ECDH) via @noble/curves — exposes both raw private
 *                 and public bytes so we can wrap the private side with the MEK.
 */

import { argon2id } from "hash-wasm";
import { x25519 } from "@noble/curves/ed25519.js";

// ----- Argon2id parameters (tuned for browser, ~0.5–1.5s) ------------------
export const ARGON2: {
  iterations: number;
  memoryKib: number;
  parallelism: number;
  hashLength: number;
} = {
  iterations: 3,
  memoryKib: 64 * 1024, // 64 MiB
  parallelism: 1,
  hashLength: 32,
};

// ----- Base64 helpers ------------------------------------------------------
export function bytesToB64(buf: Uint8Array | ArrayBuffer): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
export function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

// ----- Argon2id KDF --------------------------------------------------------
export async function deriveKeyArgon2id(
  password: string,
  saltB64: string,
  iterations = ARGON2.iterations,
  memoryKib = ARGON2.memoryKib,
  parallelism = ARGON2.parallelism,
): Promise<Uint8Array> {
  const salt = b64ToBytes(saltB64);
  const raw = await argon2id({
    password,
    salt,
    parallelism,
    iterations,
    memorySize: memoryKib,
    hashLength: ARGON2.hashLength,
    outputType: "binary",
  });
  return raw as Uint8Array;
}

async function importAesKey(rawBytes: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", rawBytes as BufferSource, { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
}

// ----- AES-256-GCM wrap/unwrap --------------------------------------------
export interface WrappedBlob {
  ciphertextB64: string;
  ivB64: string;
}

export async function aesGcmEncrypt(keyBytes: Uint8Array, plaintext: Uint8Array): Promise<WrappedBlob> {
  const key = await importAesKey(keyBytes);
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, plaintext as BufferSource);
  return { ciphertextB64: bytesToB64(ct), ivB64: bytesToB64(iv) };
}

export async function aesGcmDecrypt(keyBytes: Uint8Array, blob: WrappedBlob): Promise<Uint8Array> {
  const key = await importAesKey(keyBytes);
  const iv = b64ToBytes(blob.ivB64);
  const ct = b64ToBytes(blob.ciphertextB64);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, ct as BufferSource);
  return new Uint8Array(pt);
}

// ----- X25519 identity keypair --------------------------------------------
export interface X25519Keypair {
  privateKey: Uint8Array; // 32 bytes
  publicKey: Uint8Array; // 32 bytes
}

export function generateX25519Keypair(): X25519Keypair {
  const privateKey = randomBytes(32);
  const publicKey = x25519.getPublicKey(privateKey);
  return { privateKey, publicKey };
}

// ----- Higher-level vault helpers -----------------------------------------
export interface VaultMaterial {
  mek: Uint8Array;
  identity: X25519Keypair;
}

export async function createNewVaultMaterial(): Promise<VaultMaterial> {
  return {
    mek: randomBytes(32),
    identity: generateX25519Keypair(),
  };
}

const TEXT_ENC = new TextEncoder();
const TEXT_DEC = new TextDecoder();

export function utf8ToBytes(s: string): Uint8Array {
  return TEXT_ENC.encode(s);
}
export function bytesToUtf8(b: Uint8Array): string {
  return TEXT_DEC.decode(b);
}
